import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, X, Calendar, User, Building2, FileText, Link as LinkIcon, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import './ModuloBase.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Formulario de Gestión con datos prellenados (para Web Scraping u otras fuentes)
 */
const FormularioGestionPrelleno = ({ 
  tipoGestion, 
  nomenclatura,
  datosIniciales,
  onSuccess, 
  onCancel,
  camposEspecificos 
}) => {
  const [loading, setLoading] = useState(false);
  const [instituciones, setInstituciones] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [asesorSugerido, setAsesorSugerido] = useState(null);
  
  const [formData, setFormData] = useState({
    tipo_gestion: tipoGestion,
    institucion: datosIniciales?.institucion || '',
    nombre_asesor_cnc: datosIniciales?.nombre_asesor_cnc || '',
    nombre_asesor_instancia: datosIniciales?.nombre_asesor_instancia || '',
    fecha_asignacion: datosIniciales?.fecha_asignacion || new Date().toISOString().split('T')[0],
    fecha_vencimiento: datosIniciales?.fecha_vencimiento || '',
    descripcion: datosIniciales?.descripcion || '',
    links_referenciales: datosIniciales?.links_referenciales || [],
    estado: 'Registrado', // Estado inicial fijo
    campos_especificos: datosIniciales?.campos_especificos || {}
  });

  const [currentLink, setCurrentLink] = useState('');

  useEffect(() => {
    fetchCatalogos();
    fetchAsesorDisponible();
    
    // Si hay un link del scraping, agregarlo a links_referenciales
    if (datosIniciales?.campos_especificos?.link && !formData.links_referenciales.includes(datosIniciales.campos_especificos.link)) {
      setFormData(prev => ({
        ...prev,
        links_referenciales: [datosIniciales.campos_especificos.link, ...prev.links_referenciales]
      }));
    }
  }, []);

  // Actualizar asesor sugerido cuando cambia la institución
  useEffect(() => {
    if (formData.institucion) {
      fetchAsesorDisponible(formData.institucion);
    }
  }, [formData.institucion]);

  // Obtener asesor con menor carga de trabajo para la institución
  const fetchAsesorDisponible = async (institucion = null) => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (institucion) {
        params.institucion = institucion;
      }
      const response = await axios.get(`${API}/asignacion/asesor-cnc-disponible`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      if (response.data.asesor) {
        setAsesorSugerido(response.data);
        // Auto-asignar asesor sugerido si no hay uno seleccionado
        if (!formData.nombre_asesor_cnc) {
          setFormData(prev => ({
            ...prev,
            nombre_asesor_cnc: response.data.asesor.nombre_completo
          }));
        }
      }
    } catch (error) {
      console.error('Error al obtener asesor disponible:', error);
    }
  };

  const fetchCatalogos = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const instResponse = await axios.get(`${API}/catalogs/institutions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const institucionesActivas = (instResponse.data || []).filter(inst => inst.is_active);
      setInstituciones(institucionesActivas);

      const asesoresResponse = await axios.get(`${API}/catalogos/asesores-cnc?activo=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAsesores(asesoresResponse.data || []);
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCampoEspecifico = (field, value) => {
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        [field]: value
      }
    }));
  };

  const handleAddLink = () => {
    if (currentLink.trim()) {
      setFormData(prev => ({
        ...prev,
        links_referenciales: [...prev.links_referenciales, currentLink.trim()]
      }));
      setCurrentLink('');
    }
  };

  const handleRemoveLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links_referenciales: prev.links_referenciales.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/gestiones`, formData);
      onSuccess();
    } catch (error) {
      console.error('Error al crear gestión:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la gestión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="modulo-form" onSubmit={handleSubmit}>
      {/* Banner informativo */}
      <div className="panel" style={{ 
        background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
        borderLeft: '4px solid #2196F3',
        marginBottom: '1.5rem',
        padding: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <AlertCircle size={24} color="#2196F3" />
          <div>
            <strong style={{ color: '#1565C0' }}>Creando gestión desde Web Scraping</strong>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#1976D2' }}>
              Algunos campos han sido prellenados con la información capturada. Por favor complete los campos faltantes.
            </p>
          </div>
        </div>
      </div>

      {/* Campos Generales */}
      <div className="modulo-form-section">
        <h3 className="modulo-form-section-title">Información General</h3>
        
        <div className="form-grid">
          <div className="form-field">
            <label className="required">
              <Building2 size={16} />
              Institución del Organismo Ejecutivo
            </label>
            <select
              value={formData.institucion}
              onChange={(e) => handleChange('institucion', e.target.value)}
              required
            >
              <option value="">Seleccione una institución</option>
              {instituciones.map((inst) => (
                <option key={inst.id} value={inst.value}>
                  {inst.value}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label className="required">
              <User size={16} />
              Asesor CNC
            </label>
            <select
              value={formData.nombre_asesor_cnc}
              onChange={(e) => handleChange('nombre_asesor_cnc', e.target.value)}
              required
            >
              <option value="">Seleccione un asesor</option>
              {asesores.map((asesor) => (
                <option key={asesor.id} value={asesor.nombre_completo}>
                  {asesor.nombre_completo}
                </option>
              ))}
            </select>
            {asesorSugerido?.asesor && (
              <div style={{ 
                marginTop: '0.5rem', 
                padding: '0.5rem', 
                background: asesorSugerido.asesor.grupo_id ? '#E8F5E9' : '#FFF3E0', 
                border: asesorSugerido.asesor.grupo_id ? '1px solid #4CAF50' : '1px solid #FF9800',
                borderRadius: '4px',
                fontSize: '0.8rem'
              }}>
                <strong style={{ color: asesorSugerido.asesor.grupo_id ? '#2E7D32' : '#E65100' }}>💡 Sugerido:</strong>{' '}
                <span>{asesorSugerido.asesor.nombre_completo}</span>
                <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                  ({asesorSugerido.casos_asignados} casos asignados)
                </span>
                {asesorSugerido.asesor.grupo_nombre ? (
                  <div style={{ color: '#1976D2', marginTop: '0.25rem', fontSize: '0.75rem' }}>
                    📁 Grupo: {asesorSugerido.asesor.grupo_nombre}
                  </div>
                ) : (
                  <div style={{ color: '#F57C00', marginTop: '0.25rem', fontSize: '0.75rem' }}>
                    ⚠️ No hay asesores en el grupo de esta institución
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-field">
            <label>
              <User size={16} />
              Asesor Instancia de Probidad
            </label>
            <input
              type="text"
              value={formData.nombre_asesor_instancia}
              onChange={(e) => handleChange('nombre_asesor_instancia', e.target.value)}
              placeholder="Nombre del asesor"
            />
          </div>

          <div className="form-field">
            <label className="required">
              <Calendar size={16} />
              Fecha de Asignación
            </label>
            <input
              type="date"
              value={formData.fecha_asignacion}
              onChange={(e) => handleChange('fecha_asignacion', e.target.value)}
              required
            />
          </div>

          <div className="form-field">
            <label>
              <Calendar size={16} />
              Fecha de Vencimiento
            </label>
            <input
              type="date"
              value={formData.fecha_vencimiento}
              onChange={(e) => handleChange('fecha_vencimiento', e.target.value)}
            />
          </div>

          {/* Estado: Solo mostrar, no editable */}
          <div className="form-field">
            <label>Estado Inicial</label>
            <input
              type="text"
              value="Registrado"
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            <small style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
              El estado inicial es "Registrado". Puede cambiarlo posteriormente.
            </small>
          </div>
        </div>

        <div className="form-field" style={{ marginTop: '1rem' }}>
          <label className="required">
            <FileText size={16} />
            Descripción
          </label>
          <div style={datosIniciales?.descripcion ? { 
            border: '2px solid #2196F3', 
            borderRadius: '8px',
            backgroundColor: '#F3F9FF',
            padding: '2px'
          } : {}}>
            <RichTextEditor
              value={formData.descripcion}
              onChange={(value) => handleChange('descripcion', value)}
              placeholder="Descripción detallada del caso..."
              minHeight="150px"
            />
          </div>
          {datosIniciales?.descripcion && (
            <small style={{ color: '#2196F3', fontSize: '0.75rem' }}>
              ✓ Prellenado desde Web Scraping
            </small>
          )}
        </div>

        <div className="form-field" style={{ marginTop: '1rem' }}>
          <label>
            <LinkIcon size={16} />
            Links Referenciales
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="url"
              value={currentLink}
              onChange={(e) => setCurrentLink(e.target.value)}
              placeholder="https://ejemplo.com"
            />
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={handleAddLink}
            >
              Agregar
            </button>
          </div>
          {formData.links_referenciales.length > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              {formData.links_referenciales.map((link, index) => (
                <div 
                  key={index} 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    padding: '0.5rem',
                    background: index === 0 && datosIniciales?.campos_especificos?.link === link 
                      ? '#E3F2FD' 
                      : 'var(--color-background)',
                    borderRadius: '4px',
                    marginBottom: '0.25rem',
                    border: index === 0 && datosIniciales?.campos_especificos?.link === link 
                      ? '1px solid #2196F3' 
                      : 'none'
                  }}
                >
                  <LinkIcon size={14} />
                  <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '0.875rem' }}>
                    {link}
                  </a>
                  {index === 0 && datosIniciales?.campos_especificos?.link === link && (
                    <span style={{ fontSize: '0.7rem', color: '#2196F3', fontWeight: 'bold' }}>
                      Enlace original
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveLink(index)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer',
                      color: '#F44336'
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Campos Específicos del Módulo */}
      {camposEspecificos && (
        <div className="modulo-form-section">
          <h3 className="modulo-form-section-title">Campos Específicos - {tipoGestion}</h3>
          {camposEspecificos(formData, handleCampoEspecifico)}
        </div>
      )}

      {/* Botones de Acción */}
      <div className="form-actions">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={onCancel}
          disabled={loading}
        >
          <X size={18} />
          Cancelar
        </button>
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={loading}
        >
          <Save size={18} />
          {loading ? 'Guardando...' : 'Crear Gestión'}
        </button>
      </div>
    </form>
  );
};

export default FormularioGestionPrelleno;
